import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { COLORS } from '../lib/styleGuide';
import '../index.css';

const SignIn = ({ onSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signin, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isFormValid = email && password;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);
      await signin(email, password);
      
      // Handle redirect or callback
      const returnTo = searchParams.get('returnTo');
      if (onSuccess) {
        onSuccess();
      } else if (returnTo) {
        navigate(decodeURIComponent(returnTo), { replace: true });
      } else {
        navigate('/fooddiscovery');
      }
    } catch (error) {
      setError('Failed to sign in: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setLoading(true);
      await signInWithGoogle();
      
      // Handle redirect or callback
      const returnTo = searchParams.get('returnTo');
      if (onSuccess) {
        onSuccess();
      } else if (returnTo) {
        navigate(decodeURIComponent(returnTo), { replace: true });
      } else {
        navigate('/fooddiscovery');
      }
    } catch (error) {
      setError('Failed to sign in with Google: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="w-full max-w-md mx-auto py-12 px-4" style={{fontFamily: 'Montserrat, sans-serif'}}>
      {/* Header */}
      <h1 
        className="text-4xl font-bold mb-2 text-left" 
        style={{
          fontWeight: 700,
          background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
          backgroundSize: '200% 200%',
          animation: 'gradientShift 3s ease infinite',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}
      >
        Welcome Back
      </h1>
      <p className="text-base text-[#757575] mb-8 text-left" style={{fontWeight: 400}}>Sign in to continue</p>
      {/* Form */}
      <form className="space-y-5 w-full" onSubmit={handleSubmit}>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-2 text-center">
            {error}
          </div>
        )}
        {/* Email */}
        <div className="w-full">
          <label htmlFor="email-address" className="block text-sm font-semibold text-black mb-2 text-left" style={{fontWeight: 600}}>
            Email
          </label>
          <input
            id="email-address"
            name="email"
            type="email"
            autoComplete="email"
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C97B53] focus:border-transparent transition-all duration-200 text-base placeholder-[#bdbdbd]"
            placeholder="youremail@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{fontWeight: 500}}
          />
        </div>
        {/* Password */}
        <div className="w-full">
          <label htmlFor="password" className="block text-sm font-semibold text-black mb-2 text-left" style={{fontWeight: 600}}>
            Password
          </label>
          <input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C97B53] focus:border-transparent transition-all duration-200 text-base placeholder-[#bdbdbd]"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{fontWeight: 500}}
          />
          <div className="flex justify-end mt-1">
            <Link to="/forgot-password" className="text-sm" style={{ color: COLORS.PRIMARY_BROWN, fontWeight: 500 }}>
              Forgot password?
            </Link>
          </div>
        </div>
        {/* Sign In Button */}
        <button
          type="submit"
          disabled={loading || !email || !password}
          className="mt-2 w-full px-4 py-3 border border-transparent text-base font-medium rounded-md text-white transition-opacity"
          style={{
            backgroundColor: (!email || !password) 
              ? '#D1D5DB' 
              : COLORS.PRIMARY_BROWN,
            fontWeight: 600,
            fontSize: '1.125rem',
            borderRadius: '12px',
            cursor: (!email || !password) 
              ? 'not-allowed' 
              : 'pointer'
          }}
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>
      {/* Divider */}
      <div className="flex justify-center items-center mt-12 mb-2 w-full">
        <div className="flex-grow border-t border-gray-200" />
        <span className="mx-3 text-[#757575] font-medium text-base" style={{fontWeight: 500}}>OR</span>
        <div className="flex-grow border-t border-gray-200" />
      </div>
      {/* Social Buttons */}
      <div className="w-full">
        <button
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="w-full mb-4 px-4 py-3 border border-gray-300 rounded-xl text-base font-medium bg-white hover:bg-gray-50 transition-colors flex items-center justify-center space-x-3"
          style={{fontWeight: 600}}
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          <span className="text-gray-700">Continue with Google</span>
        </button>
        <button
          disabled
          className="w-full px-4 py-3 border border-transparent rounded-xl text-base font-medium bg-black hover:bg-gray-900 transition-colors flex items-center justify-center space-x-3"
          style={{fontWeight: 600}}
        >
          <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
          </svg>
          <span className="text-white">Continue with Apple</span>
        </button>
      </div>
      {/* Footer */}
      <div className="mt-8 text-center w-full">
        <p className="text-[#757575] text-base" style={{fontWeight: 400}}>
          Don't have an account?{' '}
          <Link to="/signup" style={{ color: COLORS.PRIMARY_BROWN, fontWeight: 500 }}>
            Join now
          </Link>
        </p>
      </div>
    </div>
  );
};

export default SignIn; 